import React, { useState, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import {
  Card,
  Button,
  Table,
  Form,
  InputGroup,
  Alert,
  Spinner,
  Modal,
  Dropdown,
  Pagination,
} from "react-bootstrap";
import { Icon } from "../../utils/iconHelper";
import StudyResourceService, { StudyResource } from "../../_services/StudyResourceService";
import RubricService, { Rubric } from "../../_services/RubricService";
import StudyResourceFormModal from "./components/StudyResourceFormModal";
import StudyResourceViewDrawer from "./components/StudyResourceViewDrawer";
import StudyResourceIssueModal from "./components/StudyResourceIssueModal";
import StudyResourceEnquiryIssueModal from "./components/StudyResourceEnquiryIssueModal";
import StudyResourceStaffIssueModal from "./components/StudyResourceStaffIssueModal";
import StudyResourceStudentIssueModal from "./components/StudyResourceStudentIssueModal";
import "./StudyResourcesPage.css";

function isS3Url(url: string): boolean {
  return url.includes(".s3.") && url.includes("amazonaws.com");
}

function isMarkedImportant(resource: StudyResource): boolean {
  if (!resource.metaData) return false;
  try {
    const metaDataObj = typeof resource.metaData === 'string'
      ? JSON.parse(resource.metaData)
      : resource.metaData;
    return metaDataObj?.markItImportant === 1;
  } catch (e) {
    return false;
  }
}

const StudyResourcesPage: React.FC = () => {
  const location = useLocation();
  const history = useHistory();
  useDocumentTitle("Study Resources");

  // State management
  const [resources, setResources] = useState<StudyResource[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [searchId, setSearchId] = useState("");
  const [filterType, setFilterType] = useState<number | undefined>(undefined);
  const [filterRagStatus, setFilterRagStatus] = useState<string>("");
  const [initialized, setInitialized] = useState(false);
  const [resourceTypes, setResourceTypes] = useState<{ id: number; name: string }[]>([]);

  // Sort & pagination states
  const [sortBy, setSortBy] = useState("lastModifiedDate");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;

  // Modal states
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingResource, setEditingResource] = useState<StudyResource | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingResource, setDeletingResource] = useState<StudyResource | null>(null);

  // Inline rubric editing states
  const [rubrics, setRubrics] = useState<Rubric[]>([]);
  const [editingRubricId, setEditingRubricId] = useState<number | null>(null);
  const [updatingRubric, setUpdatingRubric] = useState(false);

  // View drawer state
  const [showViewDrawer, setShowViewDrawer] = useState(false);
  const [viewingResource, setViewingResource] = useState<StudyResource | null>(null);

  // Issue modal state
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [issuingResource, setIssuingResource] = useState<StudyResource | null>(null);

  // Enquiry issue modal state
  const [showEnquiryIssueModal, setShowEnquiryIssueModal] = useState(false);
  const [enquiryIssuingResource, setEnquiryIssuingResource] = useState<StudyResource | null>(null);

  // Staff issue modal state
  const [showStaffIssueModal, setShowStaffIssueModal] = useState(false);
  const [staffIssuingResource, setStaffIssuingResource] = useState<StudyResource | null>(null);

  // Student issue modal state
  const [showStudentIssueModal, setShowStudentIssueModal] = useState(false);
  const [studentIssuingResource, setStudentIssuingResource] = useState<StudyResource | null>(null);

  async function openResourceUrl(url: string) {
    if (!url) return;
    if (!isS3Url(url)) {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    const newWindow = window.open("", "_blank");
    try {
      const signedUrl = await StudyResourceService.getSignedUrl(url);
      if (newWindow) newWindow.location.href = signedUrl;
    } catch {
      if (newWindow) newWindow.location.href = url;
    }
  }

  // Read URL parameters on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const idParam = params.get("id");
    if (idParam) {
      setSearchId(idParam);
    }
    // ?typeId=40 deep-links pre-filtered to a resource type (e.g. the
    // Decision Resource Studio portlet opens Interactive Pathways only)
    const typeParam = params.get("typeId");
    if (typeParam && !isNaN(parseInt(typeParam, 10))) {
      setFilterType(parseInt(typeParam, 10));
    }
    setInitialized(true);
  }, [location.search]);

  // Fetch resources after initialization and when filters/sort/page change
  useEffect(() => {
    if (initialized) {
      fetchResources();
    }
  }, [filterType, filterRagStatus, sortBy, sortOrder, currentPage, initialized, searchId]);

  // Load rubrics list for inline editing
  useEffect(() => {
    const loadRubrics = async () => {
      try {
        const response = await RubricService.getAllRubrics({ limit: 100 });
        if (response.success) {
          setRubrics(response.data.rubrics);
        }
      } catch (err) {
        console.error("Failed to load rubrics:", err);
      }
    };
    loadRubrics();
  }, []);

  // Load enabled study resource types for the Type filter
  useEffect(() => {
    StudyResourceService.getStudyResourceTypes()
      .then((types) => setResourceTypes(types))
      .catch((err) => console.error("Failed to load study resource types:", err));
  }, []);

  const fetchResources = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await StudyResourceService.getStudyResources({
        typeId: filterType,
        search: searchTerm || undefined,
        id: searchId ? Number(searchId) : undefined,
        ragStatus: filterRagStatus || undefined,
        sortBy,
        sortOrder,
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
      });
      setResources(response.data);
      setTotalCount(response.totalCount);
    } catch (err: any) {
      setError(err.message || "Failed to fetch resources");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchResources();
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  const handleCreate = () => {
    setEditingResource(null);
    setShowFormModal(true);
  };

  const handleEdit = (resource: StudyResource) => {
    setEditingResource(resource);
    setShowFormModal(true);
  };

  const handleDelete = (resource: StudyResource) => {
    setDeletingResource(resource);
    setShowDeleteModal(true);
  };

  const handleView = (resource: StudyResource) => {
    setViewingResource(resource);
    setShowViewDrawer(true);
  };

  const handleIssue = (resource: StudyResource) => {
    setIssuingResource(resource);
    setShowIssueModal(true);
  };

  const handleEnquiryIssue = (resource: StudyResource) => {
    setEnquiryIssuingResource(resource);
    setShowEnquiryIssueModal(true);
  };

  const handleStaffIssue = (resource: StudyResource) => {
    setStaffIssuingResource(resource);
    setShowStaffIssueModal(true);
  };

  const handleStudentIssue = (resource: StudyResource) => {
    setStudentIssuingResource(resource);
    setShowStudentIssueModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingResource?.id) return;

    try {
      await StudyResourceService.deleteStudyResource(deletingResource.id);
      setSuccess(`Resource "${deletingResource.name}" deleted successfully`);
      setShowDeleteModal(false);
      setDeletingResource(null);
      fetchResources();
    } catch (err: any) {
      setError(err.message || "Failed to delete resource");
    }
  };

  const handleFormSuccess = () => {
    setShowFormModal(false);
    setEditingResource(null);
    setSuccess(editingResource ? "Resource updated successfully" : "Resource created successfully");
    fetchResources();
  };

  const handleRubricChange = async (resourceId: number, rubricId: number | null) => {
    setUpdatingRubric(true);
    try {
      await StudyResourceService.updateStudyResource(resourceId, { rubricId });
      // Update the resource in state without full refetch
      setResources(prev => prev.map(r =>
        r.id === resourceId
          ? {
              ...r,
              rubricId: rubricId || undefined,
              rubricName: rubricId ? rubrics.find(rb => rb.id === rubricId)?.name : undefined
            }
          : r
      ));
      setSuccess("Rubric updated successfully");
    } catch (err: any) {
      setError(err.message || "Failed to update rubric");
    } finally {
      setUpdatingRubric(false);
      setEditingRubricId(null);
    }
  };

  const getTypeIcon = (typeId: number) => {
    const iconProps = { className: "mr-1" };
    switch (typeId) {
      case 1: return <Icon.Download {...iconProps} />;
      case 5: return <Icon.Link {...iconProps} />;
      case 6: return <Icon.FileText {...iconProps} />;
      case 31:
      case 32: return <Icon.Video {...iconProps} />;
      case 36: return <Icon.FileText {...iconProps} />; // Using FileText as a placeholder for test icon
      default: return null;
    }
  };

  const getTypePillColor = (typeId: number) => {
    switch (typeId) {
      case 1: return { bg: "#e8f0fe", color: "#1a56db" };
      case 5: return { bg: "#e0f7fa", color: "#00796b" };
      case 6: return { bg: "#e8f5e9", color: "#2e7d32" };
      case 31: return { bg: "#fff8e1", color: "#f57f17" };
      case 32: return { bg: "#fce4ec", color: "#c62828" };
      case 36: return { bg: "#f3e5f5", color: "#6a1b9a" };
      default: return { bg: "#f5f5f5", color: "#616161" };
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="study-resources-page">
      {/* Header */}
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h2 className="mb-1">Study Resources</h2>
            <p className="text-muted mb-0">Manage your educational resources</p>
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="outline-primary"
              onClick={() => history.push("/quick-quiz-maker?returnTo=study-resources")}
              className="d-flex align-items-center"
            >
              <Icon.FileText className="me-2" />
              Quick Quiz
            </Button>
            <Button
              variant="primary"
              onClick={handleCreate}
              className="d-flex align-items-center"
            >
              <Icon.Plus className="me-2" />
              Create New Resource
            </Button>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Card className="mb-4">
        <Card.Body className="filter-panel">
          <div className="filter-row">
            {/* Search */}
            <InputGroup style={{ width: '280px', flex: '0 0 auto' }}>
              <InputGroup.Text>
                <Icon.Search size={16} />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search resources..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              />
            </InputGroup>

            {/* ID Filter */}
            <InputGroup style={{ width: '130px', flex: '0 0 auto' }}>
              <InputGroup.Text>ID</InputGroup.Text>
              <Form.Control
                type="text"
                inputMode="numeric"
                placeholder="Enter ID"
                value={searchId}
                onChange={(e) => setSearchId(e.target.value.replace(/[^0-9]/g, ""))}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              />
            </InputGroup>

            {/* Type Filter */}
            <Form.Select
              value={filterType || ""}
              onChange={(e) => {
                setFilterType(e.target.value ? Number(e.target.value) : undefined);
                setCurrentPage(1);
              }}
              style={{ width: '160px', flex: '0 0 auto' }}
            >
              <option value="">All Types</option>
              {resourceTypes.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </Form.Select>

            {/* RAG Status Filter */}
            <Form.Select
              value={filterRagStatus}
              onChange={(e) => {
                setFilterRagStatus(e.target.value);
                setCurrentPage(1);
              }}
              style={{ width: '160px', flex: '0 0 auto' }}
            >
              <option value="">All RAG Status</option>
              <option value="ragified">RAG-ified</option>
              <option value="not-ragified">Not RAG-ified</option>
            </Form.Select>

            {/* Sort By */}
            <Form.Select
              value={`${sortBy}:${sortOrder}`}
              onChange={(e) => {
                const [col, order] = e.target.value.split(":");
                setSortBy(col);
                setSortOrder(order as "asc" | "desc");
                setCurrentPage(1);
              }}
              style={{ width: '190px', flex: '0 0 auto' }}
            >
              <optgroup label="Sort by">
                <option value="lastModifiedDate:desc">Modified (Newest)</option>
                <option value="lastModifiedDate:asc">Modified (Oldest)</option>
                <option value="id:desc">ID (Highest)</option>
                <option value="id:asc">ID (Lowest)</option>
                <option value="name:asc">Name (A-Z)</option>
                <option value="name:desc">Name (Z-A)</option>
                <option value="typeId:asc">Type (A-Z)</option>
                <option value="typeId:desc">Type (Z-A)</option>
              </optgroup>
            </Form.Select>

            {/* Search Button */}
            <Button variant="primary" onClick={handleSearch}>
              <Icon.Search size={14} />
              Search
            </Button>

            {/* Total Count - pushed to the right with visual separator */}
            <div className="results-count">
              <strong>{totalCount.toLocaleString()}</strong> results
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Resources Table */}
      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <Spinner role="status" animation="border" variant="primary" />
              <p className="mt-3 text-muted">Loading resources...</p>
            </div>
          ) : resources.length === 0 ? (
            <div className="text-center py-5">
              <Icon.FileText size={48} className="text-muted mb-3" />
              <h5 className="text-muted">No resources found</h5>
              <p className="text-muted">Create your first resource to get started</p>
              <Button variant="primary" onClick={handleCreate} className="mt-3">
                <Icon.Plus className="me-2" />
                Create Resource
              </Button>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover>
                <thead>
                  <tr>
                    {[
                      { column: "id", label: "ID", width: "60px" },
                      { column: "name", label: "Name", width: undefined },
                      { column: "typeId", label: "Type", width: "100px" },
                      { column: "rubricId", label: "Rubric", width: "120px" },
                      { column: "hasAiConfig", label: "AI Workspace", width: "110px" },
                      { column: "modules", label: "Modules", width: "180px" },
                      { column: "lastModifiedDate", label: "Modified", width: "110px" },
                    ].map(({ column, label, width }) => (
                      <th
                        key={column}
                        style={{ width, whiteSpace: "nowrap" }}
                      >
                        {label}
                      </th>
                    ))}
                    <th style={{ width: "160px", whiteSpace: "nowrap" }} className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {resources.map((resource) => (
                    <tr key={resource.id} onClick={() => handleView(resource)} style={{ cursor: 'pointer' }}>
                      <td>
                        <div className="d-flex align-items-center">
                          {isMarkedImportant(resource) && (
                            <Icon.Star
                              size={14}
                              style={{
                                color: 'var(--status-warning)',
                                fill: 'var(--status-warning)',
                                marginRight: '6px',
                                flexShrink: 0
                              }}
                              title="Marked as Important"
                            />
                          )}
                          <span className="text-muted">{resource.id}</span>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          {getTypeIcon(resource.typeId)}
                          <div>
                            <div className="fw-semibold" style={{ color: 'var(--text-strong)' }}>
                              {resource.name}
                              {resource.isRagified && (
                                <span style={{ color: 'var(--brand-accent)', fontSize: '10px', marginLeft: '4px', lineHeight: 1 }} title="RAG-ified">✦</span>
                              )}
                            </div>
                            {resource.url && !resource.url.startsWith("/custom-objects/") && !resource.url.startsWith("judge0://") && (
                              <small className="text-muted text-truncate d-block" style={{ maxWidth: "300px" }}>
                                {resource.url}
                              </small>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span style={{
                          display: "inline-block",
                          padding: "4px 10px",
                          borderRadius: "12px",
                          fontSize: "12px",
                          fontWeight: 500,
                          backgroundColor: getTypePillColor(resource.typeId).bg,
                          color: getTypePillColor(resource.typeId).color,
                        }}>
                          {resource.typeName || StudyResourceService.getResourceTypeName(resource.typeId)}
                        </span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {editingRubricId === resource.id ? (
                          <Form.Select
                            size="sm"
                            value={resource.rubricId || ""}
                            onChange={(e) => handleRubricChange(resource.id!, e.target.value ? Number(e.target.value) : null)}
                            onBlur={() => setEditingRubricId(null)}
                            autoFocus
                            disabled={updatingRubric}
                            style={{ minWidth: '120px', fontSize: '12px' }}
                          >
                            <option value="">No rubric</option>
                            {rubrics.map((rubric) => (
                              <option key={rubric.id} value={rubric.id}>
                                {rubric.name}
                              </option>
                            ))}
                          </Form.Select>
                        ) : (
                          <div
                            onClick={() => setEditingRubricId(resource.id!)}
                            style={{ cursor: 'pointer' }}
                          >
                            {resource.rubricName ? (
                              <span
                                title={`${resource.rubricName} — click to change`}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 4,
                                  maxWidth: 160,
                                  padding: "3px 8px 3px 7px",
                                  borderRadius: 6,
                                  fontSize: 12,
                                  fontWeight: 500,
                                  backgroundColor: "#fff8e1",
                                  color: "var(--status-warning-text)",
                                  border: "1px solid var(--status-warning-border)",
                                  lineHeight: 1.3,
                                }}
                              >
                                <Icon.CheckCircle size={11} style={{ flexShrink: 0, color: 'var(--status-warning)' }} />
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {resource.rubricName}
                                </span>
                              </span>
                            ) : (
                              <span className="text-muted" style={{ fontSize: '12px' }} title="Click to set a rubric">
                                <Icon.Plus size={12} className="me-1" />
                                Set
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td>
                        <a
                          href={`/ai-workspace-config?resourceId=${resource.id}&resourceName=${encodeURIComponent(resource.name)}`}
                          title="Click to configure AI"
                          style={{ textDecoration: 'none' }}
                        >
                          {resource.hasAiConfig ? (
                            <span style={{ display: "inline-block", padding: "4px 10px", borderRadius: "12px", fontSize: "12px", fontWeight: 500, backgroundColor: "#e8f5e9", color: "#2e7d32" }}>
                              Enabled
                            </span>
                          ) : (
                            <span className="text-muted" style={{ fontSize: '12px' }}>
                              +Set
                            </span>
                          )}
                        </a>
                      </td>
                      <td>
                        {resource.learningModules && resource.learningModules.length > 0 ? (
                          <div style={{ fontSize: '12px', lineHeight: '1.4' }}>
                            {resource.learningModules.map((module, idx) => (
                              <a
                                key={module.id}
                                href={`/lm-builder/${module.id}?mode=view`}
                                className="d-block text-primary text-truncate"
                                style={{ maxWidth: '170px', textDecoration: 'none' }}
                                title={module.name}
                              >
                                {module.name}
                              </a>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted" style={{ fontSize: '12px' }}>—</span>
                        )}
                      </td>
                      <td className="text-muted">
                        {formatDate(resource.lastModifiedDate)}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="d-flex justify-content-end" style={{ gap: '8px' }}>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleEdit(resource)}
                            title="Edit resource"
                          >
                            <Icon.Edit2 className="mr-1" />
                            Edit
                          </Button>
                          <Dropdown align="end">
                            <Dropdown.Toggle variant="secondary" size="sm">
                              <Icon.MoreVertical />
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item onClick={() => handleView(resource)}>
                                <Icon.Eye size={14} className="mr-1" />
                                View Details
                              </Dropdown.Item>
                              {resource.url && (
                                <Dropdown.Item
                                  onClick={() => openResourceUrl(resource.url!)}
                                >
                                  <Icon.ExternalLink size={14} className="mr-1" />
                                  Open URL
                                </Dropdown.Item>
                              )}
                              <Dropdown.Item onClick={() => handleIssue(resource)}>
                                <Icon.Send size={14} className="mr-1" />
                                Issue to Batches
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => handleEnquiryIssue(resource)}>
                                <Icon.Send size={14} className="mr-1" />
                                Issue to Enquiry Level
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => handleStaffIssue(resource)}>
                                <Icon.Send size={14} className="mr-1" />
                                Issue to Staff
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => handleStudentIssue(resource)}>
                                <Icon.Send size={14} className="mr-1" />
                                Issue to Students
                              </Dropdown.Item>
                              <Dropdown.Divider />
                              <Dropdown.Item
                                className="text-danger"
                                onClick={() => handleDelete(resource)}
                              >
                                <Icon.Trash2 size={14} className="mr-1" />
                                Delete
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
          {totalCount > pageSize && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <span className="text-muted" style={{ fontSize: "14px" }}>
                Showing {Math.min((currentPage - 1) * pageSize + 1, totalCount)}
                &ndash;{Math.min(currentPage * pageSize, totalCount)} of {totalCount}
              </span>
              <Pagination className="mb-0">
                <Pagination.First
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                />
                <Pagination.Prev
                  onClick={() => setCurrentPage((p) => p - 1)}
                  disabled={currentPage === 1}
                />
                {Array.from(
                  { length: Math.ceil(totalCount / pageSize) },
                  (_, i) => i + 1
                )
                  .filter((page) => {
                    const totalPages = Math.ceil(totalCount / pageSize);
                    return (
                      page === 1 ||
                      page === totalPages ||
                      Math.abs(page - currentPage) <= 2
                    );
                  })
                  .reduce<(number | "ellipsis")[]>((acc, page, idx, arr) => {
                    if (idx > 0 && page - (arr[idx - 1] as number) > 1) {
                      acc.push("ellipsis");
                    }
                    acc.push(page);
                    return acc;
                  }, [])
                  .map((item, idx) =>
                    item === "ellipsis" ? (
                      <Pagination.Ellipsis key={`ellipsis-${idx}`} disabled />
                    ) : (
                      <Pagination.Item
                        key={item}
                        active={item === currentPage}
                        onClick={() => setCurrentPage(item as number)}
                      >
                        {item}
                      </Pagination.Item>
                    )
                  )}
                <Pagination.Next
                  onClick={() => setCurrentPage((p) => p + 1)}
                  disabled={currentPage === Math.ceil(totalCount / pageSize)}
                />
                <Pagination.Last
                  onClick={() => setCurrentPage(Math.ceil(totalCount / pageSize))}
                  disabled={currentPage === Math.ceil(totalCount / pageSize)}
                />
              </Pagination>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Form Modal */}
      <StudyResourceFormModal
        show={showFormModal}
        onHide={() => setShowFormModal(false)}
        resource={editingResource}
        onSuccess={handleFormSuccess}
      />

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered dialogClassName="study-resources-modal">
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this resource?</p>
          {deletingResource && (
            <Alert variant="warning" className="mb-0">
              <strong>{deletingResource.name}</strong>
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* View Drawer */}
      <StudyResourceViewDrawer
        show={showViewDrawer}
        onHide={() => setShowViewDrawer(false)}
        resource={viewingResource}
        onEdit={handleEdit}
        onIssue={handleIssue}
      />

      {/* Issue to Batches Modal */}
      <StudyResourceIssueModal
        show={showIssueModal}
        onHide={() => setShowIssueModal(false)}
        resourceId={issuingResource?.id ?? null}
        resourceName={issuingResource?.name ?? ""}
      />

      {/* Issue to Enquiry Level Modal (includes history inside) */}
      <StudyResourceEnquiryIssueModal
        show={showEnquiryIssueModal}
        onHide={() => setShowEnquiryIssueModal(false)}
        resourceId={enquiryIssuingResource?.id ?? null}
        resourceName={enquiryIssuingResource?.name ?? ""}
      />

      {/* Issue to Staff Modal (includes history inside) */}
      <StudyResourceStaffIssueModal
        show={showStaffIssueModal}
        onHide={() => setShowStaffIssueModal(false)}
        resourceId={staffIssuingResource?.id ?? null}
        resourceName={staffIssuingResource?.name ?? ""}
      />

      {/* Issue to Students Modal (includes history inside) */}
      <StudyResourceStudentIssueModal
        show={showStudentIssueModal}
        onHide={() => setShowStudentIssueModal(false)}
        resourceId={studentIssuingResource?.id ?? null}
        resourceName={studentIssuingResource?.name ?? ""}
      />
    </div>
  );
};

export default StudyResourcesPage;
